import { Predicates } from "libraries/predicates/predicates";
import { retrieveObjectFromLocalStorage } from "./local-storage.helper";
import {
  ArtworkCollectionPersistentData,
  ArtworkMetaPersistentData,
  ArtworkPrintingPersistentData,
} from "types/persistent-page-filters";
import { MasterArtworkNameState } from "../states/meta-artwork/artwork-name/data.type";
import { MasterArtworkNumberState } from "../states/meta-artwork/artwork-number/data.type";
import { MasterArtworkPrintingTechniqueState } from "../states/meta-artwork/printing-technique/data.type";
import { MasterArtworkCollectionState } from "../states/meta-artwork/collections/data.type";

export const buildMetaArtworkSearchFilterObject = (
  state: MasterArtworkNumberState,
) => {
  return {
    id: state.id ? state.id : null,
    artwork_name: state.artwork_name ? state.artwork_name : null,
    code: state.code ? state.code : null,
    collection: state.collection ? state.collection?.id?.toString() : null,
    launch_date: state.launch_date
      ? state.launch_date
      : null,
    searchType: "ArtworkNumber",
  };
};

export const buildArtworkPrintingTechniqueSearchFilterObject = (
  state: MasterArtworkPrintingTechniqueState,
) => {
  const molds_id: number[] = [];
  if (state.molds) {
    state.molds.forEach((mold) => molds_id.push(Number(mold.id)));
  }

  return {
    name: state.name ? state.name : null,
    code: state.code ? state.code : null,
    id: state.id ? state.id : null,
    artwork_name: state.artwork_name ? state.artwork_name : null,
    artwork_number: state.artwork_number ? state.artwork_number : null,
    collection: state.collection ? state.collection?.id?.toString() : null,
    color: state.color ? state.color.name : null,
    molds: molds_id.length ? molds_id : null,
    launch_date: state.artwork_name?.launch_date
      ? state.artwork_name?.launch_date
      : null,
    decoration_technique: state.decoration_technique
      ? state.decoration_technique?.id
      : null,
    searchType: "PrintingTechnique",
  };
};

export const buildArtworkCollectionSearchFilterObject = (
  state: MasterArtworkCollectionState,
) => {
  return {
    code: state.code ? state.code : null,
    // global_product_id: state.global_product ? state.global_product?.id?.toString() : null,
    name: state.name ? state.name.toString() : null,
    licensor: state.licensor ? state.licensor.id?.toString() : null,
    licensed_property: state.licensed_property
      ? state.licensed_property.id?.toString()
      : null,
    launch_date: state.launch_date ? state.launch_date : null,
    status: state.status ? state.status.toString() : null,
    searchType: "Collection",
  };
};

export const formatPrintingTechniqueOptions = (
  item: MasterArtworkPrintingTechniqueState | null,
) => {
  return Predicates.isNotNullAndNotUndefined(item)
    ? {
        value: item,
        label: `${item.id?.toString().padStart(5, "0")} - ${item.name}`,
      }
    : null;
};

export const getPrintingTechniques = (
  printingTechniques: MasterArtworkPrintingTechniqueState[],
) =>
  printingTechniques
    .map(formatPrintingTechniqueOptions)
    .filter(Predicates.isNotNullAndNotUndefined);

export const getOperatorAndValue = (storedFilters: any, keyPrefix: string) => {
  for (let key in storedFilters) {
    if (key.includes(keyPrefix)) {
      const releaseDateKeySplit = key.split("__");
      let label = "";
      switch (releaseDateKeySplit[1]) {
        case "gt":
          label = ">";
          break;
        case "gte":
          label = ">=";
          break;
        case "lt":
          label = "<";
          break;
        case "lte":
          label = "<=";
          break;
        default:
          label = "=";
          break;
      }
      return {
        operator: { label: label, value: key },
        value: storedFilters[key] ?? "",
      };
    }
  }
};

export const buildArtworkRequestTextFiltersBasedOnLocalStorage = (
  prefix:
    | ArtworkCollectionPersistentData["prefix"]
    | ArtworkMetaPersistentData["prefix"]
    | ArtworkPrintingPersistentData["prefix"],
): Partial<MasterArtworkNameState> => {
  const storedFilters = retrieveObjectFromLocalStorage<
    | ArtworkCollectionPersistentData
    | ArtworkMetaPersistentData
    | ArtworkPrintingPersistentData
  >(prefix, "lastFilters");
  if (storedFilters) {
    return {
      launch_date: storedFilters.launch_date ?? "",
    };
  } else {
    return {};
  }
};

export const buildArtworkTextFiltersBasedOnLocalStorage = (
  prefix:
    | ArtworkCollectionPersistentData["prefix"]
    | ArtworkMetaPersistentData["prefix"]
    | ArtworkPrintingPersistentData["prefix"],
): Partial<MasterArtworkNameState> => {
  const storedFilters = retrieveObjectFromLocalStorage<
    | ArtworkCollectionPersistentData
    | ArtworkMetaPersistentData
    | ArtworkPrintingPersistentData
  >(prefix, "lastFilters");
  if (storedFilters) {
    const piecesSellingUnitsValues = getOperatorAndValue(
      storedFilters,
      "pieces_selling_units",
    );
    return {
      launch_date: storedFilters.launch_date ?? "",
    };
  } else {
    return {};
  }
};

export const buildDropdownFiltersBasedOnLocalStorage = (
  prefix:
    | ArtworkCollectionPersistentData["prefix"]
    | ArtworkMetaPersistentData["prefix"]
    | ArtworkPrintingPersistentData["prefix"],
): Partial<MasterArtworkNameState> => {
  const storedDropdownFilters = retrieveObjectFromLocalStorage<
    | ArtworkCollectionPersistentData
    | ArtworkMetaPersistentData
    | ArtworkPrintingPersistentData
  >(prefix, "lastDropdownFilters");
  if (storedDropdownFilters) {
    return {
      code: storedDropdownFilters.code ?? null,
      name: storedDropdownFilters.name ?? null,
      //licensor: storedDropdownFilters.licensor ?? null,
      //licensed_property: storedDropdownFilters.licensedProperty ?? null,
      status: storedDropdownFilters.status ?? null,
      //global_product: storedDropdownFilters.globalProduct ?? null,
    };
  } else {
    return {};
  }
};
